/**
 * Build a `/cdn` URL with an optional on-demand resize width.
 *
 * The worker keeps the original in R2 untouched and serves a quality-preserved,
 * smaller WebP derivative when `?w=` is present (Cloudflare Images, edge-cached).
 * Omit `w` to get the full-resolution original (e.g. download links).
 *
 * Accepts either a bare R2 key (`weddings/foo/media/x.jpg`) or an already-built
 * `/cdn/...` URL (e.g. live-pushed slideshow media), so callers can pass whatever
 * the API handed them.
 *
 * Allowed widths mirror the worker whitelist (64/480/800/1280/2048); other
 * values are snapped server-side, but stick to these to maximise cache hits.
 *
 * The `&v=` token is a CLIENT cache-buster: a window where transforms briefly
 * 500'd with `Cache-Control: immutable` poisoned browser HTTP/Service-Worker
 * caches for a year. Bumping CDN_VERSION changes every transformed URL so
 * clients fetch fresh past their stale entry — no per-device cache clear needed.
 * (Server is fixed; this only orphans already-cached bad responses.) The worker
 * ignores unknown query params, so `v` doesn't affect transform/cache behaviour.
 */
const CDN_VERSION = "2";

export function cdnUrl(keyOrUrl: string, opts?: { w?: number }): string {
  const base = keyOrUrl.startsWith("/cdn/") || keyOrUrl.startsWith("http")
    ? keyOrUrl
    : `/cdn/${keyOrUrl}`;
  if (!opts?.w) return base;
  const sep = base.includes("?") ? "&" : "?";
  return `${base}${sep}w=${opts.w}&v=${CDN_VERSION}`;
}
